<template>
  <v-dialog v-model="localVisible" max-width="1180px" persistent scrollable>
    <v-card>
      <v-card-title class="primary white--text">
        <div>
          <div>{{ player?.display_name || 'Detalle de handicap' }}</div>
          <div class="text-caption">{{ player?.membership_number || 'Sin número de socio' }}</div>
        </div>
        <v-spacer />
        <v-btn icon dark @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <div v-if="detailLoading" class="text-center py-12">
          <v-progress-circular indeterminate color="primary" size="48" />
        </div>
        <template v-else-if="overview">
          <div class="d-flex flex-wrap align-center mb-4 management-actions">
            <v-btn small color="primary" class="mr-2 mb-2" @click="openRoundCapture()">
              <v-icon left small>mdi-scorecard-outline</v-icon>Capturar ronda histórica
            </v-btn>
            <v-btn small outlined color="primary" class="mr-2 mb-2" @click="openProfileDialog">
              <v-icon left small>mdi-account-edit</v-icon>Editar perfil
            </v-btn>
            <v-btn small outlined color="indigo" class="mr-2 mb-2" @click="openExternalDialog">
              <v-icon left small>mdi-database-import</v-icon>Registrar índice externo
            </v-btn>
            <v-btn
              v-if="overview.player.handicap_authority === 'ghin'"
              small outlined color="orange" class="mb-2"
              :loading="operationLoading"
              @click="useLocalCalculation"
            >
              <v-icon left small>mdi-calculator</v-icon>Volver a cálculo local
            </v-btn>
          </div>
          <v-row>
            <v-col cols="12" md="3" class="text-center">
              <div class="handicap-large">{{ currentIndex }}</div>
              <div class="grey--text">Índice vigente</div>
              <v-chip class="mt-2" :color="workflowColor" dark small>{{ workflowLabel }}</v-chip>
            </v-col>
            <v-col cols="12" md="9">
              <v-row dense>
                <v-col cols="6" sm="3">
                  <v-card outlined><v-card-text class="text-center">
                    <div class="text-h5 font-weight-bold">{{ overview.workflow.eligible_differentials }}</div>
                    <div class="text-caption">Diferenciales</div>
                  </v-card-text></v-card>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-card outlined><v-card-text class="text-center">
                    <div class="text-h5 font-weight-bold">{{ overview.workflow.differentials_needed }}</div>
                    <div class="text-caption">Faltantes</div>
                  </v-card-text></v-card>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-card outlined><v-card-text class="text-center">
                    <div class="text-h5 font-weight-bold">{{ overview.workflow.unpaired_nine_hole_rounds }}</div>
                    <div class="text-caption">9H sin pareja</div>
                  </v-card-text></v-card>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-card outlined><v-card-text class="text-center">
                    <div class="text-h5 font-weight-bold">{{ overview.round_counts.draft }}</div>
                    <div class="text-caption">Por validar</div>
                  </v-card-text></v-card>
                </v-col>
              </v-row>
              <v-alert class="mt-3 mb-0" :type="workflowAlertType" dense outlined>
                {{ workflowMessage }}
              </v-alert>
            </v-col>
          </v-row>

          <v-row v-if="previewCalculation" class="mt-2">
            <v-col cols="12">
              <v-card color="blue lighten-5" outlined>
                <v-card-title class="subtitle-1">
                  Vista previa #{{ previewCalculation.revision }}
                  <v-spacer />
                  <v-chip color="primary" dark>Índice {{ previewCalculation.final_index }}</v-chip>
                </v-card-title>
                <v-card-text>
                  {{ previewCalculation.used_rounds_count }} de {{ previewCalculation.eligible_rounds_count }} diferenciales utilizados.
                  Vigencia: {{ formatDate(previewCalculation.effective_on, false) }}.
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" :loading="operationLoading" @click="publishPreview">
                    <v-icon left>mdi-check-decagram</v-icon>Publicar índice
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>

          <v-tabs v-model="activeTab" class="mt-5">
            <v-tab>Rondas</v-tab>
            <v-tab>Historial</v-tab>
            <v-tab>Cálculos</v-tab>

            <v-tab-item>
              <v-data-table :headers="roundHeaders" :items="overview.recent_rounds" hide-default-footer>
                <template v-slot:item.played_on="{ item }">{{ formatDate(item.played_on, false) }}</template>
                <template v-slot:item.course="{ item }">{{ item.course_name }} · {{ item.tee_name }}</template>
                <template v-slot:item.score="{ item }">
                  {{ item.adjusted_gross_score ?? item.gross_score ?? '--' }}
                </template>
                <template v-slot:item.score_differential="{ item }">
                  {{ item.score_differential === null ? '--' : Number(item.score_differential).toFixed(1) }}
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip :color="roundStatusColor(item.status)" x-small dark>{{ roundStatusLabel(item.status) }}</v-chip>
                </template>
                <template v-slot:item.control="{ item }">
                  <div class="d-flex flex-column align-start py-1">
                    <span class="text-caption">{{ roundSourceLabel(item.source) }}</span>
                    <v-chip v-if="item.paired_round" x-small outlined color="primary">9H emparejada</v-chip>
                    <v-chip v-if="Number(item.committee_adjustment)" x-small outlined color="warning" class="mt-1">
                      Comité {{ signedNumber(item.committee_adjustment) }}
                    </v-chip>
                  </div>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn v-if="item.status === 'draft'" icon small color="success" title="Validar" @click.stop="validateRound(item)">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="canPair(item)"
                    icon small color="primary" title="Emparejar vuelta de 9 hoyos"
                    @click.stop="openPairDialog(item)"
                  ><v-icon small>mdi-link-variant</v-icon></v-btn>
                  <v-btn
                    v-if="item.status === 'validated'"
                    icon small color="warning" title="Ajuste de comité"
                    @click.stop="openAdjustmentDialog(item)"
                  ><v-icon small>mdi-tune-variant</v-icon></v-btn>
                  <v-btn v-if="item.status === 'validated'" icon small color="error" title="Anular" @click.stop="askInvalidate(item)">
                    <v-icon small>mdi-cancel</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="item.status === 'invalidated'"
                    icon small color="primary" title="Capturar reemplazo"
                    @click.stop="openRoundCapture(item)"
                  ><v-icon small>mdi-file-replace-outline</v-icon></v-btn>
                </template>
              </v-data-table>
            </v-tab-item>

            <v-tab-item>
              <HandicapChart v-if="overview.history.length" :history="overview.history" />
              <v-data-table :headers="historyHeaders" :items="overview.history" hide-default-footer>
                <template v-slot:item.effective_on="{ item }">{{ formatDate(item.effective_on, false) }}</template>
                <template v-slot:item.source="{ item }">{{ sourceLabel(item.source) }}</template>
              </v-data-table>
            </v-tab-item>

            <v-tab-item>
              <v-expansion-panels accordion flat>
                <v-expansion-panel v-for="calculation in overview.calculations" :key="calculation.id">
                  <v-expansion-panel-header>
                    Revisión {{ calculation.revision }} · {{ calculation.final_index }} · {{ calculationStatusLabel(calculation.status) }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <thead><tr><th>Fecha</th><th>Diferencial</th><th>Seleccionado</th></tr></thead>
                      <tbody>
                        <tr v-for="round in calculation.round_selections" :key="round.source_round_id">
                          <td>{{ formatDate(round.played_on, false) }}</td>
                          <td>{{ round.differential }}</td>
                          <td><v-icon :color="round.selected ? 'success' : 'grey'">{{ round.selected ? 'mdi-check' : 'mdi-minus' }}</v-icon></td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-tab-item>
          </v-tabs>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-btn
          v-if="canCalculate"
          color="primary"
          outlined
          :loading="operationLoading"
          @click="calculatePreview"
        ><v-icon left>mdi-calculator</v-icon>Calcular vista previa</v-btn>
        <v-spacer />
        <v-btn text @click="$emit('close')">Cerrar</v-btn>
        <v-btn color="primary" text :loading="detailLoading" @click="loadDetail">
          <v-icon left>mdi-refresh</v-icon>Refrescar
        </v-btn>
      </v-card-actions>

      <v-dialog v-model="invalidateDialog" max-width="480px">
        <v-card>
          <v-card-title>Anular ronda</v-card-title>
          <v-card-text>
            <v-textarea v-model="invalidationReason" label="Motivo" outlined rows="3" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="invalidateDialog = false">Cancelar</v-btn>
            <v-btn color="error" :disabled="!invalidationReason.trim()" :loading="operationLoading" @click="invalidateSelectedRound">Anular</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="profileDialog" max-width="620px">
        <v-card>
          <v-card-title>Editar perfil del golfista</v-card-title>
          <v-card-text>
            <v-form ref="profileForm" v-model="profileValid">
              <v-autocomplete
                v-model="profileIdentityKey"
                :search-input.sync="identitySearch"
                :items="profileIdentityOptions"
                :item-text="identityOptionLabel"
                item-value="key"
                item-disabled="disabled"
                label="Socio vinculado"
                outlined dense clearable
                :loading="identityLoading"
                hint="La vinculación evita expedientes duplicados al capturar desde la app"
                persistent-hint
                @change="applyProfileIdentity"
              />
              <v-text-field v-model="profileForm.display_name" label="Nombre para mostrar" outlined dense :rules="requiredRules" />
              <v-text-field v-model="profileForm.membership_number" label="Número de socio" outlined dense />
              <v-row>
                <v-col cols="12" sm="6"><v-select v-model="profileForm.gender" :items="genderOptions" label="Categoría" outlined dense /></v-col>
                <v-col cols="12" sm="6"><v-select v-model="profileForm.status" :items="statusOptions" label="Estado" outlined dense /></v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="profileDialog = false">Cancelar</v-btn>
            <v-btn color="primary" :disabled="!profileValid" :loading="operationLoading" @click="saveProfile">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="externalDialog" max-width="540px">
        <v-card>
          <v-card-title>Registrar índice externo</v-card-title>
          <v-card-text>
            <v-alert type="info" dense outlined>El perfil pasará a autoridad GHIN y el cálculo local dejará de publicarse mientras permanezca así.</v-alert>
            <v-form ref="externalForm" v-model="externalValid">
              <v-text-field v-model="externalForm.handicap_index" type="number" step="0.1" min="-10" max="54" label="Índice externo" outlined dense :rules="indexRules" />
              <v-text-field v-model="externalForm.effective_on" type="date" label="Fecha de vigencia" outlined dense :rules="requiredRules" />
              <v-textarea v-model="externalForm.notes" label="Notas / referencia" outlined rows="2" />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="externalDialog = false">Cancelar</v-btn>
            <v-btn color="indigo" dark :disabled="!externalValid" :loading="operationLoading" @click="saveExternalIndex">Registrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="pairDialog" max-width="650px">
        <v-card>
          <v-card-title>Emparejar vueltas de 9 hoyos</v-card-title>
          <v-card-text>
            <v-alert type="info" dense outlined>Las dos vueltas formarán un diferencial elegible de 18 hoyos.</v-alert>
            <v-select
              v-model="pairedRoundId"
              :items="pairCandidates"
              :item-text="pairCandidateLabel"
              item-value="id"
              label="Segunda vuelta validada"
              outlined
              :loading="pairCandidatesLoading"
              no-data-text="No hay otra vuelta de 9 hoyos disponible"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="pairDialog = false">Cancelar</v-btn>
            <v-btn color="primary" :disabled="!pairedRoundId" :loading="operationLoading" @click="pairSelectedRound">Emparejar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="adjustmentDialog" max-width="520px">
        <v-card>
          <v-card-title>Ajuste de comité</v-card-title>
          <v-card-text>
            <v-alert type="warning" dense outlined>El ajuste modifica el diferencial de esta ronda y queda registrado en auditoría.</v-alert>
            <v-text-field v-model="adjustmentValue" type="number" step="0.1" min="-20" max="20" label="Ajuste" outlined dense />
            <v-textarea v-model="adjustmentReason" label="Motivo obligatorio" outlined rows="3" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="adjustmentDialog = false">Cancelar</v-btn>
            <v-btn color="warning" :disabled="!adjustmentReady" :loading="operationLoading" @click="adjustSelectedRound">Aplicar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <HandicapRoundDialog
        :visible="roundDialog"
        :player="overview && overview.player"
        :configuration="configuration"
        :source-round="replacementRound"
        :saving="operationLoading"
        @close="closeRoundCapture"
        @save="saveRound"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { handicapAPI } from '@/services/handicap'
import HandicapChart from './HandicapChart.vue'
import HandicapRoundDialog from './HandicapRoundDialog.vue'

export default {
  name: 'HandicapDetailDialog',
  components: { HandicapChart, HandicapRoundDialog },
  props: {
    visible: { type: Boolean, default: false },
    player: { type: Object, default: null }
  },
  data () {
    return {
      activeTab: 0,
      invalidateDialog: false,
      roundToInvalidate: null,
      invalidationReason: '',
      profileDialog: false,
      profileValid: false,
      profileForm: { display_name: '', membership_number: '', gender: 'unspecified', status: 'active', user: null, ccslp_user: null },
      identityOptions: [],
      identityLoading: false,
      identitySearch: '',
      identitySearchTimer: null,
      profileIdentityKey: null,
      externalDialog: false,
      externalValid: false,
      externalForm: { handicap_index: '', effective_on: '', notes: '' },
      pairDialog: false,
      roundToPair: null,
      pairedRoundId: null,
      pairCandidates: [],
      pairCandidatesLoading: false,
      adjustmentDialog: false,
      roundToAdjust: null,
      adjustmentValue: 0,
      adjustmentReason: '',
      roundDialog: false,
      replacementRound: null,
      requiredRules: [value => Boolean(value) || 'Campo requerido'],
      indexRules: [
        value => (value !== '' && value !== null && value !== undefined) || 'Campo requerido',
        value => (Number(value) >= -10 && Number(value) <= 54) || 'El índice debe estar entre -10.0 y 54.0'
      ],
      genderOptions: [
        { text: 'Caballeros', value: 'male' },
        { text: 'Damas', value: 'female' },
        { text: 'Sin especificar', value: 'unspecified' }
      ],
      statusOptions: [
        { text: 'Activo', value: 'active' },
        { text: 'Inactivo', value: 'inactive' }
      ],
      roundHeaders: [
        { text: 'Fecha', value: 'played_on' },
        { text: 'Cancha / tee', value: 'course' },
        { text: 'Hoyos', value: 'holes_played' },
        { text: 'Score ajustado', value: 'score' },
        { text: 'Diferencial', value: 'score_differential' },
        { text: 'Estado', value: 'status' },
        { text: 'Control', value: 'control', sortable: false },
        { text: '', value: 'actions', sortable: false }
      ],
      historyHeaders: [
        { text: 'Vigencia', value: 'effective_on' },
        { text: 'Índice', value: 'handicap_index' },
        { text: 'Índice bajo', value: 'low_handicap_index' },
        { text: 'Origen', value: 'source' },
        { text: 'Notas', value: 'notes' }
      ]
    }
  },
  computed: {
    ...mapState('handicap', ['selectedOverview', 'configuration', 'detailLoading', 'operationLoading']),
    overview () { return this.selectedOverview },
    localVisible: {
      get () { return this.visible },
      set (value) { if (!value) this.$emit('close') }
    },
    currentIndex () {
      const value = this.overview?.player.current_handicap_index
      return value === null || value === undefined ? 'NH' : value
    },
    previewCalculation () {
      return this.overview?.calculations.find(item => item.status === 'preview') || null
    },
    canCalculate () {
      if (!this.overview) return false
      return ['ready_for_preview', 'published'].includes(this.overview.workflow.state) && this.overview.workflow.eligible_differentials >= 3
    },
    adjustmentReady () {
      const value = Number(this.adjustmentValue)
      return this.adjustmentReason.trim() && Number.isFinite(value) && value >= -20 && value <= 20
    },
    profileIdentityOptions () {
      const currentProfileId = this.overview?.player?.id
      return this.identityOptions.map(option => ({
        ...option,
        disabled: Boolean(option.linked_profile_id && option.linked_profile_id !== currentProfileId)
      }))
    },
    workflowLabel () {
      return ({ inactive: 'Inactivo', external: 'Externo', nh: 'NH', ready_for_preview: 'Listo para calcular', preview_pending: 'Pendiente de publicar', published: 'Publicado' })[this.overview?.workflow.state] || '--'
    },
    workflowColor () {
      return ({ inactive: 'grey', external: 'indigo', nh: 'orange', ready_for_preview: 'blue', preview_pending: 'warning', published: 'success' })[this.overview?.workflow.state] || 'grey'
    },
    workflowAlertType () {
      return ['nh', 'preview_pending'].includes(this.overview?.workflow.state) ? 'warning' : 'info'
    },
    workflowMessage () {
      const workflow = this.overview?.workflow
      if (!workflow) return ''
      if (workflow.state === 'inactive') return 'El perfil está inactivo; no se generarán cálculos.'
      if (workflow.state === 'external') return 'El índice de este socio es administrado por GHIN.'
      if (workflow.state === 'nh') return `Aún no tiene handicap. Faltan ${workflow.differentials_needed} diferencial(es) elegible(s).`
      if (workflow.state === 'preview_pending') return 'Existe una vista previa lista para revisión y publicación.'
      if (workflow.state === 'ready_for_preview') return 'Las rondas necesarias están listas para generar una vista previa.'
      return 'El índice vigente está publicado. Puede recalcularse cuando entren nuevas rondas.'
    }
  },
  watch: {
    visible (value) { if (value && this.player) this.loadDetail() },
    player (value) { if (this.visible && value) this.loadDetail() },
    identitySearch (value) {
      if (!this.profileDialog) return
      if (this.identitySearchTimer) clearTimeout(this.identitySearchTimer)
      this.identitySearchTimer = setTimeout(() => this.loadIdentityOptions(value), 250)
    }
  },
  beforeDestroy () {
    if (this.identitySearchTimer) clearTimeout(this.identitySearchTimer)
  },
  methods: {
    ...mapActions('handicap', ['fetchPlayerOverview', 'runOperation']),
    loadDetail () {
      if (!this.player) return Promise.resolve()
      return this.fetchPlayerOverview(this.player.id)
    },
    async execute (payload) {
      try {
        await this.runOperation({ playerId: this.player.id, ...payload })
        this.$emit('updated')
        return true
      } catch (error) {
        // El módulo conserva el mensaje para mostrarlo en la pantalla principal.
        return false
      }
    },
    calculatePreview () { return this.execute({ operation: 'calculate' }) },
    publishPreview () { return this.execute({ operation: 'publish', calculationId: this.previewCalculation.id }) },
    validateRound (round) { return this.execute({ operation: 'validate', roundId: round.id }) },
    askInvalidate (round) {
      this.roundToInvalidate = round
      this.invalidationReason = ''
      this.invalidateDialog = true
    },
    async invalidateSelectedRound () {
      if (await this.execute({ operation: 'invalidate', roundId: this.roundToInvalidate.id, reason: this.invalidationReason })) {
        this.invalidateDialog = false
      }
    },
    async openProfileDialog () {
      const profile = this.overview.player
      this.profileForm = {
        display_name: profile.display_name || '',
        membership_number: profile.membership_number || '',
        gender: profile.gender || 'unspecified',
        status: profile.status || 'active',
        user: profile.user || null,
        ccslp_user: profile.ccslp_user || null
      }
      this.profileIdentityKey = profile.user
        ? `user:${profile.user}`
        : (profile.ccslp_user ? `ccslp_user:${profile.ccslp_user}` : null)
      this.identitySearch = ''
      this.profileDialog = true
      await this.loadIdentityOptions()
      this.$nextTick(() => this.$refs.profileForm?.resetValidation())
    },
    async loadIdentityOptions (search = '') {
      this.identityLoading = true
      try {
        const params = search?.trim() ? { search: search.trim() } : {}
        const response = await handicapAPI.listIdentityOptions(params)
        this.identityOptions = response.data?.results || response.data || []
      } finally {
        this.identityLoading = false
      }
    },
    identityOptionLabel (option) {
      const number = option.membership_number ? ` · ${option.membership_number}` : ''
      const used = option.disabled ? ' · ya vinculado' : ''
      return `${option.display_name}${number}${used}`
    },
    applyProfileIdentity (key) {
      const option = this.identityOptions.find(item => item.key === key)
      this.profileForm.user = option?.type === 'user' ? option.id : null
      this.profileForm.ccslp_user = option?.type === 'ccslp_user' ? option.id : null
      if (!option) return
      this.profileForm.display_name = option.display_name
      if (option.membership_number) this.profileForm.membership_number = option.membership_number
    },
    async saveProfile () {
      if (!this.$refs.profileForm.validate()) return
      if (await this.execute({ operation: 'updateProfile', data: this.profileForm })) this.profileDialog = false
    },
    today () {
      const date = new Date()
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset())
      return date.toISOString().slice(0, 10)
    },
    openExternalDialog () {
      this.externalForm = {
        handicap_index: this.overview.player.external_handicap_index ?? this.overview.player.current_handicap_index ?? '',
        effective_on: this.today(),
        notes: ''
      }
      this.externalDialog = true
      this.$nextTick(() => this.$refs.externalForm?.resetValidation())
    },
    async saveExternalIndex () {
      if (!this.$refs.externalForm.validate()) return
      if (await this.execute({
        operation: 'externalIndex',
        data: { ...this.externalForm, handicap_index: Number(this.externalForm.handicap_index) }
      })) this.externalDialog = false
    },
    useLocalCalculation () {
      return this.execute({ operation: 'updateProfile', data: { handicap_authority: 'local' } })
    },
    openRoundCapture (sourceRound = null) {
      this.replacementRound = sourceRound
      this.roundDialog = true
    },
    closeRoundCapture () {
      this.roundDialog = false
      this.replacementRound = null
    },
    async saveRound (data) {
      if (await this.execute({ operation: 'createRound', data })) this.closeRoundCapture()
    },
    canPair (round) {
      return round.status === 'validated' && round.holes_played === 9 && !round.paired_round
    },
    async openPairDialog (round) {
      this.roundToPair = round
      this.pairedRoundId = null
      this.pairCandidates = []
      this.pairDialog = true
      this.pairCandidatesLoading = true
      try {
        const response = await handicapAPI.listRounds({ player: this.player.id, status: 'validated' })
        const data = response.data?.results || response.data || []
        this.pairCandidates = data.filter(item => item.id !== round.id && item.holes_played === 9 && !item.paired_round)
      } finally {
        this.pairCandidatesLoading = false
      }
    },
    pairCandidateLabel (round) {
      return `${this.formatDate(round.played_on, false)} · ${round.course_name} / ${round.tee_name} · ${round.adjusted_gross_score ?? round.gross_score ?? '--'} golpes`
    },
    async pairSelectedRound () {
      if (await this.execute({ operation: 'pair', roundId: this.roundToPair.id, pairedRoundId: this.pairedRoundId })) {
        this.pairDialog = false
      }
    },
    openAdjustmentDialog (round) {
      this.roundToAdjust = round
      this.adjustmentValue = round.committee_adjustment || 0
      this.adjustmentReason = ''
      this.adjustmentDialog = true
    },
    async adjustSelectedRound () {
      if (!this.adjustmentReady) return
      if (await this.execute({
        operation: 'adjust',
        roundId: this.roundToAdjust.id,
        adjustment: Number(this.adjustmentValue),
        reason: this.adjustmentReason
      })) this.adjustmentDialog = false
    },
    roundStatusColor (status) { return ({ draft: 'warning', validated: 'success', invalidated: 'error' })[status] || 'grey' },
    roundStatusLabel (status) { return ({ draft: 'Borrador', validated: 'Validada', invalidated: 'Anulada' })[status] || status },
    roundSourceLabel (source) { return ({ manual: 'Captura RP', golf_score: 'Tarjeta', import: 'Importación' })[source] || source },
    signedNumber (value) { return Number(value) > 0 ? `+${value}` : value },
    sourceLabel (source) { return ({ calculated: 'Calculado', ghin: 'GHIN', manual: 'Manual', import: 'Importación' })[source] || source },
    calculationStatusLabel (status) { return ({ preview: 'Vista previa', published: 'Publicado', superseded: 'Reemplazado' })[status] || status },
    formatDate (date, withTime = true) {
      if (!date) return '--'
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      if (withTime && String(date).includes('T')) Object.assign(options, { hour: '2-digit', minute: '2-digit' })
      return new Date(date).toLocaleDateString('es-MX', options)
    }
  }
}
</script>

<style scoped>
.handicap-large { font-size: 58px; line-height: 1; font-weight: 700; color: #1976d2; }
.management-actions { gap: 2px; }
</style>
